import { useState, useEffect } from 'react'
import Mic from '../../assets/svgs/mic'
import {
  DeviceControlSettingsButton,
  DeviceSettingsAudioLevelBar,
  DeviceSettingsAudioLevelContainer,
  DeviceSettingsDropdownContainer,
  DeviceSettingsDropdownContent,
  DeviceSettingsSection,
  DeviceSettingsSectionHeader,
  DeviceSettingsSelect,
} from '../styled'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import Gear from '../../assets/svgs/Gear'
import CameraOn from '../../assets/svgs/cameraOn'
import Speaker from '../../assets/svgs/Speaker'

export function DeviceSettings({ open, onOpenChange, isMobile }) {
  const { t } = useTranslation()
  const [devices, setDevices] = useState({
    cameras: [],
    microphones: [],
    audioOutputs: [],
  })
  const [selectedDevices, setSelectedDevices] = useState({
    camera: '',
    microphone: '',
    audioOutput: '',
  })
  const [audioLevel, setAudioLevel] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((deviceInfos) => {
      const cameras = deviceInfos.filter(
        (device) => device.kind === 'videoinput',
      )
      const microphones = deviceInfos.filter(
        (device) => device.kind === 'audioinput',
      )
      const audioOutputs = deviceInfos.filter(
        (device) => device.kind === 'audiooutput',
      )
      setDevices({ cameras, microphones, audioOutputs })
      setLoading(false)
    })

    let audioContext
    let analyser
    let animationFrame

    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      audioContext = new (window.AudioContext || window.webkitAudioContext)()
      analyser = audioContext.createAnalyser()
      const microphone = audioContext.createMediaStreamSource(stream)
      microphone.connect(analyser)
      analyser.fftSize = 256
      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      const updateAudioLevel = () => {
        if (analyser) {
          analyser.getByteFrequencyData(dataArray)
          const volume = Math.max(...dataArray)
          setAudioLevel((volume / 256) * 100)
          animationFrame = requestAnimationFrame(updateAudioLevel)
        }
      }

      updateAudioLevel()
    })

    return () => {
      if (audioContext) audioContext.close()
      if (animationFrame) cancelAnimationFrame(animationFrame)
    }
  }, [])

  const handleDeviceChange = (type, deviceId) => {
    setSelectedDevices((prev) => ({ ...prev, [type]: deviceId }))
  }

  return (
    <DeviceSettingsDropdownContainer>
      <DeviceControlSettingsButton onClick={() => onOpenChange(!open)}>
        <Gear
          width={isMobile ? 16 : 24}
          height={isMobile ? 16 : 24}
          style={{ marginRight: !isMobile && '4px' }}
        />
        {!isMobile && t('preInterview.deviceSettings')}
      </DeviceControlSettingsButton>
      <DeviceSettingsDropdownContent isOpen={open} isMobile={isMobile}>
        <DeviceSettingsSection>
          <DeviceSettingsSectionHeader>
            <CameraOn width={14} height={14} />
            {t('preInterview.camera')}
          </DeviceSettingsSectionHeader>
          <DeviceSettingsSelect
            value={selectedDevices.camera}
            onChange={(e) => handleDeviceChange('camera', e.target.value)}
            disabled={loading}
            style={{ fontSize: isMobile ? '11px' : '12px' }}
          >
            {devices.cameras.map((camera) => (
              <option key={camera.deviceId} value={camera.deviceId}>
                {camera.label ||
                  `Camera ${devices.cameras.indexOf(camera) + 1}`}
              </option>
            ))}
          </DeviceSettingsSelect>
        </DeviceSettingsSection>

        <DeviceSettingsSection>
          <DeviceSettingsSectionHeader>
            <Mic width={14} height={14} />
            {t('preInterview.microphone')}
          </DeviceSettingsSectionHeader>
          <DeviceSettingsSelect
            value={selectedDevices.microphone}
            onChange={(e) => handleDeviceChange('microphone', e.target.value)}
            disabled={loading}
            style={{ fontSize: isMobile ? '11px' : '12px' }}
          >
            {devices.microphones.map((mic) => (
              <option key={mic.deviceId} value={mic.deviceId}>
                {mic.label ||
                  `Microphone ${devices.microphones.indexOf(mic) + 1}`}
              </option>
            ))}
          </DeviceSettingsSelect>
          <DeviceSettingsAudioLevelContainer>
            <DeviceSettingsAudioLevelBar level={audioLevel} />
          </DeviceSettingsAudioLevelContainer>
        </DeviceSettingsSection>

        <DeviceSettingsSection>
          <DeviceSettingsSectionHeader>
            <Speaker width={14} height={14} />
            {t('preInterview.audioOutput')}
          </DeviceSettingsSectionHeader>
          <DeviceSettingsSelect
            value={selectedDevices.audioOutput}
            onChange={(e) => handleDeviceChange('audioOutput', e.target.value)}
            disabled={loading}
            style={{ fontSize: isMobile ? '11px' : '12px' }}
          >
            {devices.audioOutputs.map((output) => (
              <option key={output.deviceId} value={output.deviceId}>
                {output.label ||
                  `Speaker ${devices.audioOutputs.indexOf(output) + 1}`}
              </option>
            ))}
          </DeviceSettingsSelect>
        </DeviceSettingsSection>
      </DeviceSettingsDropdownContent>
    </DeviceSettingsDropdownContainer>
  )
}

DeviceSettings.propTypes = {
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  isMobile: PropTypes.bool,
}

DeviceSettings.defaultProps = {
  open: false,
  onOpenChange: () => {},
  isMobile: false,
}
